import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalCloseButton,
	ModalBody,
} from "@chakra-ui/react";
import type { HelpTopic } from "./types";

export interface HelpTopicChooserProps {
	/** Controls the chooser's open state. */
	isOpen: boolean;
	/** Topics to list, in display order. */
	topics: HelpTopic[];
	/** Fired when the chooser is dismissed without a topic being chosen. */
	onClose: () => void;
	/** Fired with the chosen topic. The caller is responsible for starting its tour. */
	onSelect: (topic: HelpTopic) => void;
}

/**
 * HelpTopicChooser — "What would you like to know more about?"
 *
 * Presented by `Help` in place of starting a tour immediately when it is
 * given `topics` instead of a flat `guideSteps` list. Purely presentational:
 * selecting a card just reports the choice back to the caller, which is
 * responsible for closing the chooser and starting the corresponding tour
 * (see `Help`) — Joyride must never measure targets while this modal's
 * overlay is still covering the page.
 */
export const HelpTopicChooser: React.FC<HelpTopicChooserProps> = ({
	isOpen,
	topics,
	onClose,
	onSelect,
}) => {
	return (
		<Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
			<ModalOverlay />
			<ModalContent data-testid="help-topic-chooser" borderRadius="xl">
				<ModalHeader className="pr-10 text-base font-semibold text-gray-900">
					What would you like to know more about?
				</ModalHeader>
				<ModalCloseButton data-testid="help-topic-chooser-close" />
				<ModalBody className="pb-6">
					<div role="list" className="flex flex-col gap-2">
						{topics.map((topic) => (
							<button
								key={topic.id}
								type="button"
								role="listitem"
								data-testid={`help-topic-${topic.id}`}
								onClick={() => onSelect(topic)}
								className="flex items-start gap-3 rounded-lg border border-gray-200 px-4 py-3 text-left transition-colors hover:border-blue-300 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-[#0031DD]/40"
							>
								{topic.icon && (
									<span className="mt-0.5 text-lg text-[#0031DD]">{topic.icon}</span>
								)}
								<span className="flex flex-col">
									<span className="text-sm font-semibold text-gray-900">
										{topic.title}
									</span>
									{topic.description && (
										<span className="text-sm text-gray-500">{topic.description}</span>
									)}
								</span>
							</button>
						))}
					</div>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
};
